import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Scale, TrendingDown, Activity, Droplets, Dumbbell, Moon, Calendar, Target, FileText, ArrowLeft, User, Edit, Pill, ClipboardList, Mail, Phone, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import { formatDateBR, formatDateShortBR, getTodayString } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Modal de Registro de Progresso
const ProgressModal = ({ isOpen, onClose, patientId, patientName, onSuccess }) => {
  const today = getTodayString();
  const [formData, setFormData] = useState({
    data: today,
    peso: '',
    agua_ml: '',
    humor: '',
    atividade_fisica: '',
    horas_sono: ''
  });
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [measurements, setMeasurements] = useState({
    cintura: '',
    quadril: '',
    peito: '',
    braco: '',
    perna: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Preparar dados do progresso
      const progressData = {
        patient_id: patientId,
        data: formData.data,
        peso: formData.peso ? parseFloat(formData.peso) : null,
        agua_ml: formData.agua_ml ? parseInt(formData.agua_ml) : null,
        humor: formData.humor || null,
        atividade_fisica: formData.atividade_fisica || null,
        horas_sono: formData.horas_sono ? parseFloat(formData.horas_sono) : null
      };

      // Salvar progresso via endpoint admin
      await axios.post(`${API_URL}/api/admin/patients/${patientId}/progress`, progressData);

      // Se tiver medidas, salvar também
      if (showMeasurements && (measurements.cintura || measurements.quadril || measurements.peito || measurements.braco || measurements.perna)) {
        const measurementData = {
          patient_id: patientId,
          data: formData.data,
          cintura: measurements.cintura ? parseFloat(measurements.cintura) : null,
          quadril: measurements.quadril ? parseFloat(measurements.quadril) : null,
          peito: measurements.peito ? parseFloat(measurements.peito) : null,
          braco: measurements.braco ? parseFloat(measurements.braco) : null,
          perna: measurements.perna ? parseFloat(measurements.perna) : null
        };
        await axios.post(`${API_URL}/api/admin/patients/${patientId}/measurements`, measurementData);
      }

      toast.success('Progresso registrado com sucesso!');
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        data: today,
        peso: '',
        agua_ml: '',
        humor: '',
        atividade_fisica: '',
        horas_sono: ''
      });
      setMeasurements({
        cintura: '',
        quadril: '',
        peito: '',
        braco: '',
        perna: ''
      });
      setShowMeasurements(false);
    } catch (error) {
      console.error('Erro ao salvar progresso:', error);
      toast.error('Erro ao salvar progresso');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#0F2B4C] to-[#B8977A] p-5 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Registrar Progresso</h2>
              <p className="text-white/80 text-sm">{patientName}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X className="text-white" size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Data */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar size={16} className="inline mr-2 text-[#0F2B4C]" />
              Data
            </label>
            <input
              type="date"
              value={formData.data}
              onChange={(e) => setFormData({...formData, data: e.target.value})}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50 focus:border-[#0F2B4C]"
            />
          </div>

          {/* Peso e Água */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Scale size={16} className="inline mr-2 text-[#B8977A]" />
                Peso (kg)
              </label>
              <input
                type="number"
                step="0.1"
                placeholder="Ex: 85.5"
                value={formData.peso}
                onChange={(e) => setFormData({...formData, peso: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50 focus:border-[#0F2B4C]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Droplets size={16} className="inline mr-2 text-[#0F2B4C]" />
                Água (ml)
              </label>
              <input
                type="number"
                placeholder="Ex: 2000"
                value={formData.agua_ml}
                onChange={(e) => setFormData({...formData, agua_ml: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50 focus:border-[#0F2B4C]"
              />
            </div>
          </div>

          {/* Humor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              😊 Humor
            </label>
            <select
              value={formData.humor}
              onChange={(e) => setFormData({...formData, humor: e.target.value})}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50 focus:border-[#0F2B4C] bg-white"
            >
              <option value="">Selecione...</option>
              <option value="excelente">😄 Excelente</option>
              <option value="bom">🙂 Bom</option>
              <option value="normal">😐 Normal</option>
              <option value="ruim">😞 Ruim</option>
              <option value="pessimo">😢 Péssimo</option>
            </select>
          </div>

          {/* Atividade Física */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Dumbbell size={16} className="inline mr-2 text-[#B8977A]" />
              Atividade Física
            </label>
            <input
              type="text"
              placeholder="Ex: Caminhada 30min, Academia 1h"
              value={formData.atividade_fisica}
              onChange={(e) => setFormData({...formData, atividade_fisica: e.target.value})}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50 focus:border-[#0F2B4C]"
            />
          </div>

          {/* Horas de Sono */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Moon size={16} className="inline mr-2 text-[#0F2B4C]" />
              Horas de Sono
            </label>
            <input
              type="number"
              step="0.5"
              placeholder="Ex: 8"
              value={formData.horas_sono}
              onChange={(e) => setFormData({...formData, horas_sono: e.target.value})}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50 focus:border-[#0F2B4C]"
            />
          </div>

          {/* Toggle Medidas */}
          <button
            type="button"
            onClick={() => setShowMeasurements(!showMeasurements)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <span className="text-sm font-medium text-gray-700">
              📏 Registrar Medidas do Dia (opcional)
            </span>
            {showMeasurements ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {/* Medidas Expandíveis */}
          {showMeasurements && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Cintura (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={measurements.cintura}
                    onChange={(e) => setMeasurements({...measurements, cintura: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Quadril (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={measurements.quadril}
                    onChange={(e) => setMeasurements({...measurements, quadril: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Peito (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={measurements.peito}
                    onChange={(e) => setMeasurements({...measurements, peito: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Braço (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={measurements.braco}
                    onChange={(e) => setMeasurements({...measurements, braco: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Perna (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={measurements.perna}
                    onChange={(e) => setMeasurements({...measurements, perna: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-[#0F2B4C] to-[#B8977A] text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminPacienteDashboard = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentProgress, setRecentProgress] = useState([]);
  const [evolutionTable, setEvolutionTable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProgressModal, setShowProgressModal] = useState(false);

  useEffect(() => {
    fetchPatientData();
  }, [patientId]);

  const fetchPatientData = async () => {
    try {
      const patientRes = await axios.get(`${API_URL}/api/admin/patients/${patientId}`);
      const patientData = patientRes.data.patient || patientRes.data;
      setPatient(patientData);

      const statsRes = await axios.get(`${API_URL}/api/admin/patients/${patientId}/stats`);
      setStats(statsRes.data);

      const progressRes = await axios.get(`${API_URL}/api/admin/patients/${patientId}/progress`);
      setRecentProgress(progressRes.data.slice(0, 7).reverse());

      const evolutionRes = await axios.get(`${API_URL}/api/admin/patients/${patientId}/evolution`);
      const duration = patientData?.duracao_tratamento || 6;
      const totalDoses = duration * 4;
      const existingData = evolutionRes.data || [];
      const table = [];
      for (let i = 1; i <= totalDoses; i++) {
        const existing = existingData.find(e => e.dose_num === i) || {};
        table.push({
          dose_num: i,
          dose_mg: existing.dose_mg || '',
          data_aplicacao: existing.data_aplicacao || '',
          peso: existing.peso || '',
          med_quadril: existing.med_quadril || '',
          med_abdome: existing.med_abdome || ''
        });
      }
      setEvolutionTable(table);

    } catch (error) {
      console.error('Erro ao carregar dados do paciente:', error);
      toast.error('Erro ao carregar dados do paciente');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#B8977A', '#E5E7EB'];

  const progressPercentage = stats ? 
    Math.min(100, Math.max(0, ((stats.peso_inicial - stats.peso_atual) / (stats.peso_inicial - stats.peso_meta)) * 100)) : 0;

  const pieData = [
    { name: 'Perdido', value: progressPercentage },
    { name: 'Restante', value: Math.max(0, 100 - progressPercentage) }
  ];

  // Função para formatar data sem problemas de timezone
  const formatDate = (dateStr) => {
    return formatDateShortBR(dateStr);
  };

  const formatDateFull = (dateStr) => {
    return formatDateBR(dateStr);
  };

  const pesoData = recentProgress.map(p => ({
    data: formatDate(p.data),
    peso: p.peso
  })).filter(p => p.peso);

  const getIMCCategoria = (imc) => {
    if (!imc) return { texto: '-', cor: 'text-gray-500', bgCor: 'bg-gray-100' };
    const value = parseFloat(imc);
    if (value < 18.5) return { texto: 'Abaixo', cor: 'text-blue-600', bgCor: 'bg-blue-100' };
    if (value < 25) return { texto: 'Normal', cor: 'text-green-600', bgCor: 'bg-green-100' };
    if (value < 30) return { texto: 'Sobrepeso', cor: 'text-yellow-600', bgCor: 'bg-yellow-100' };
    return { texto: 'Obesidade', cor: 'text-red-600', bgCor: 'bg-red-100' };
  };

  const imcCategoria = getIMCCategoria(stats?.imc);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f0f2f5] via-[#f5f7fa] to-[#eef1f5] p-4 sm:p-6">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f0f2f5] via-[#f5f7fa] to-[#eef1f5] p-4 sm:p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-bold text-muted-foreground">Paciente não encontrado</h2>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-[#0F2B4C] hover:underline"
          >
            Voltar para o painel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f2f5] via-[#f5f7fa] to-[#eef1f5] p-4 sm:p-6 lg:p-8">
      {/* Modal de Progresso */}
      <ProgressModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        patientId={patientId}
        patientName={patient?.nome}
        onSuccess={fetchPatientData}
      />

      <div className="max-w-7xl mx-auto space-y-5 sm:space-y-6">
        
        {/* Header com título e botão editar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-white/50 rounded-xl transition-colors"
            >
              <ArrowLeft size={24} className="text-muted-foreground" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">Perfil do Paciente</h1>
              <p className="text-sm text-muted-foreground">Visualização completa dos dados</p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/admin/paciente/${patientId}/perfil`)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#B8977A] text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all"
          >
            <Edit size={16} />
            Editar Perfil
          </button>
        </div>

        {/* Card de perfil do paciente */}
        <div className="floating-card-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[#0F2B4C] to-[#B8977A] p-6 sm:p-8">
            <div className="flex items-center gap-4">
              {patient.foto_perfil ? (
                <div className="relative">
                  <img 
                    src={patient.foto_perfil} 
                    alt={patient.nome}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-4 border-white/30"
                  />
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
              ) : (
                <div className="relative">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 flex items-center justify-center border-4 border-white/30">
                    <User size={32} className="text-white" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
              )}
              <div className="text-white">
                <h2 className="text-xl sm:text-2xl font-bold">{patient.nome}</h2>
                <p className="text-white/80 text-sm">
                  Paciente • Cadastrado em {patient.created_at ? formatDateBR(patient.created_at) : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Informações Pessoais e Botões de Ação */}
        <div className="floating-card p-5 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <h3 className="text-lg font-bold text-highlight">Informações Pessoais</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowProgressModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#0F2B4C] text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all"
              >
                <TrendingDown size={16} />
                Registrar Progresso
              </button>
              <button
                onClick={() => navigate(`/admin/paciente/${patientId}/receitas`)}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#7CB342] text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all"
              >
                <Pill size={16} />
                Receitas
              </button>
              <button
                onClick={() => navigate(`/admin/paciente/${patientId}/plano`)}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#5C6BC0] text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all"
              >
                <ClipboardList size={16} />
                Plano Alimentar
              </button>
            </div>
          </div>
          
          {/* Campos de informação */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl">
              <div className="w-10 h-10 rounded-xl bg-[#0F2B4C]/10 flex items-center justify-center">
                <Mail size={18} className="text-[#0F2B4C]" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-sm font-medium">{patient.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl">
              <div className="w-10 h-10 rounded-xl bg-[#B8977A]/10 flex items-center justify-center">
                <Calendar size={18} className="text-[#B8977A]" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Data de Nascimento</p>
                <p className="text-sm font-medium">
                  {patient.data_nascimento ? formatDateBR(patient.data_nascimento) : '-'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl">
              <div className="w-10 h-10 rounded-xl bg-[#0F2B4C]/10 flex items-center justify-center">
                <Phone size={18} className="text-[#0F2B4C]" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Telefone</p>
                <p className="text-sm font-medium">{patient.telefone || '-'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards - 6 em uma linha no desktop */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {/* Card 1: Peso Inicial */}
          <div className="floating-card p-4 sm:p-5 float-transition">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#B8977A]/20 to-[#B8977A]/5 flex items-center justify-center">
                <Scale className="text-[#B8977A]" size={18} />
              </div>
              <span className="text-[10px] sm:text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium">
                Início
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-1 font-medium">Peso Inicial</p>
            <p className="text-2xl sm:text-3xl font-bold text-highlight">
              {stats?.peso_inicial?.toFixed(1) || '--'}
            </p>
            <p className="text-[10px] text-muted-foreground">kg</p>
          </div>

          {/* Card 2: Peso Atual */}
          <div className="floating-card p-4 sm:p-5 float-transition">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0F2B4C]/20 to-[#0F2B4C]/5 flex items-center justify-center">
                <Scale className="text-[#0F2B4C]" size={18} />
              </div>
              {stats?.peso_inicial && stats?.peso_atual && (
                <span className="text-[10px] sm:text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                  -{(stats.peso_inicial - stats.peso_atual).toFixed(1)}kg
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-1 font-medium">Peso Atual</p>
            <p className="text-2xl sm:text-3xl font-bold text-highlight">
              {stats?.peso_atual?.toFixed(1) || '--'}
            </p>
            <p className="text-[10px] text-muted-foreground">kg</p>
          </div>

          {/* Card 3: Meta de Peso */}
          <div className="floating-card p-4 sm:p-5 float-transition">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#B8977A]/20 to-[#B8977A]/5 flex items-center justify-center">
                <Target className="text-[#B8977A]" size={18} />
              </div>
              <span className="text-[10px] sm:text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                Meta
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-1 font-medium">Meta de Peso</p>
            <p className="text-2xl sm:text-3xl font-bold text-highlight">
              {stats?.peso_meta?.toFixed(1) || '--'}
            </p>
            <p className="text-[10px] text-muted-foreground">kg</p>
          </div>

          {/* Card 4: IMC */}
          <div className="floating-card p-4 sm:p-5 float-transition">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0F2B4C]/20 to-[#0F2B4C]/5 flex items-center justify-center">
                <Activity className="text-[#0F2B4C]" size={18} />
              </div>
              <span className={`text-[10px] sm:text-xs px-2 py-1 rounded-full font-medium ${imcCategoria.cor} ${imcCategoria.bgCor}`}>
                {imcCategoria.texto}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-1 font-medium">IMC</p>
            <p className="text-2xl sm:text-3xl font-bold text-highlight">
              {stats?.imc || '--'}
            </p>
            <p className="text-[10px] text-muted-foreground">Índice de Massa</p>
          </div>

          {/* Card 5: Evolução */}
          <div className="floating-card p-4 sm:p-5 float-transition">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#B8977A]/20 to-[#B8977A]/5 flex items-center justify-center">
                <TrendingDown className="text-[#B8977A]" size={18} />
              </div>
              <span className="text-[10px] sm:text-xs bg-[#B8977A]/15 text-[#7A5E42] px-2 py-1 rounded-full font-medium">
                {progressPercentage.toFixed(0)}%
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-1 font-medium">Evolução</p>
            <p className="text-2xl sm:text-3xl font-bold text-highlight">
              {progressPercentage.toFixed(0)}%
            </p>
            <p className="text-[10px] text-muted-foreground">do objetivo</p>
          </div>

          {/* Card 6: Falta */}
          <div className="floating-card p-4 sm:p-5 float-transition">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0F2B4C]/20 to-[#0F2B4C]/5 flex items-center justify-center">
                <Target className="text-[#0F2B4C]" size={18} />
              </div>
              <span className="text-[10px] sm:text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                Restante
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-1 font-medium">Falta</p>
            <p className="text-2xl sm:text-3xl font-bold text-highlight">
              {stats?.peso_atual && stats?.peso_meta ? (stats.peso_atual - stats.peso_meta).toFixed(1) : '--'}
            </p>
            <p className="text-[10px] text-muted-foreground">kg para meta</p>
          </div>
        </div>

        {/* Gráficos lado a lado */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Gráfico de Evolução Semanal */}
          <div className="floating-card p-5 sm:p-6">
            <h3 className="text-base sm:text-lg font-bold mb-4 text-highlight">
              Evolução Semanal
            </h3>
            {pesoData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={pesoData}>
                  <defs>
                    <linearGradient id="colorPesoAdmin" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#B8977A" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#B8977A" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="data" stroke="#737373" style={{ fontSize: '10px' }} tick={{ fontSize: 10 }} />
                  <YAxis stroke="#737373" style={{ fontSize: '10px' }} tick={{ fontSize: 10 }} width={35} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Area type="monotone" dataKey="peso" stroke="#B8977A" strokeWidth={2} fillOpacity={1} fill="url(#colorPesoAdmin)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                Nenhum dado de progresso disponível
              </div>
            )}
          </div>

          {/* Meta de Emagrecimento com gráfico circular */}
          <div className="floating-card p-5 sm:p-6">
            <h3 className="text-base sm:text-lg font-bold mb-4 text-highlight">
              Meta de Emagrecimento
            </h3>
            <div className="flex items-center justify-center">
              <div className="relative">
                <ResponsiveContainer width={180} height={180}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={75}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-highlight">
                      {progressPercentage.toFixed(0)}%
                    </p>
                    <p className="text-xs text-muted-foreground">completo</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="text-center p-4 bg-muted/50 rounded-xl">
                <p className="text-xl sm:text-2xl font-bold text-highlight">{stats?.peso_inicial || '--'}</p>
                <p className="text-xs text-muted-foreground">Inicial</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-xl">
                <p className="text-xl sm:text-2xl font-bold text-highlight">{stats?.peso_meta || '--'}</p>
                <p className="text-xs text-muted-foreground">Meta</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabela de Evolução */}
        <div className="floating-card p-5 sm:p-6 overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-bold text-highlight flex items-center gap-2">
              <FileText size={20} />
              Tabela de Evolução / Aplicação
            </h3>
            <button
              onClick={() => navigate(`/admin/paciente/${patientId}/perfil`)}
              className="flex items-center gap-2 px-4 py-2 bg-[#0F2B4C] text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all"
            >
              <Edit size={16} />
              Editar
            </button>
          </div>
          
          <div className="w-full overflow-x-auto pb-2">
            <table className="border-collapse text-sm w-full" style={{ minWidth: 'max-content' }}>
              <thead>
                <tr>
                  <th className="bg-gradient-to-r from-[#B8977A] to-[#A98B6B] text-white p-3 text-left text-xs sm:text-sm font-semibold rounded-tl-xl whitespace-nowrap">
                    Dados
                  </th>
                  {evolutionTable.map((item, index) => (
                    <th key={index} className={`bg-gradient-to-r from-[#B8977A] to-[#A98B6B] text-white p-3 text-center text-xs sm:text-sm font-semibold whitespace-nowrap ${index === evolutionTable.length - 1 ? 'rounded-tr-xl' : ''}`}>
                      {index + 1}ª Dose
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-medium bg-muted/30 whitespace-nowrap">Dose (mg)</td>
                  {evolutionTable.map((item, index) => (
                    <td key={index} className="p-3 text-center">{item.dose_mg || '-'}</td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-medium bg-muted/30 whitespace-nowrap">Data</td>
                  {evolutionTable.map((item, index) => (
                    <td key={index} className="p-3 text-center">
                      {item.data_aplicacao ? formatDate(item.data_aplicacao) : '-'}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-medium bg-muted/30 whitespace-nowrap">Peso (kg)</td>
                  {evolutionTable.map((item, index) => (
                    <td key={index} className="p-3 text-center font-medium text-[#B8977A]">{item.peso || '-'}</td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-medium bg-muted/30 whitespace-nowrap">Quadril (cm)</td>
                  {evolutionTable.map((item, index) => (
                    <td key={index} className="p-3 text-center">{item.med_quadril || '-'}</td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-medium bg-muted/30 whitespace-nowrap rounded-bl-xl">Abdome (cm)</td>
                  {evolutionTable.map((item, index) => (
                    <td key={index} className={`p-3 text-center ${index === evolutionTable.length - 1 ? 'rounded-br-xl' : ''}`}>{item.med_abdome || '-'}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            * Tratamento de {patient.duracao_tratamento || 6} meses × 4 doses = {(patient.duracao_tratamento || 6) * 4} doses totais
          </p>
        </div>

      </div>
    </div>
  );
};

export default AdminPacienteDashboard;
